import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux'
import { AnimatePresence } from 'framer-motion';

import { Animation, ListTile, Check, Paper, Switch, Dropdown } from 'components'
import { ToggleButton } from 'features'
import { FaAngleUp } from 'react-icons/fa'

import { itemContext } from 'context';
import * as actionCreators from 'store/actions';

const Container = styled.div`
    border-radius: 5px;
    background-color: ${props => props.styles.secondaryColor};
    max-height: 50vh;
    width: 15rem;
    overflow-y: auto;
    padding: 0.5rem;
    border: 1px solid var(--primary-accent-color);
`

const Line = styled.hr`
    border-color: var(--secondary-accent-color);
`;

const Title = styled.div`
    display: flex;
    height: min-content;
    width: 100%;
    align-items: center;
    cursor: pointer;
    background-color: ${props => props.styles.secondaryColor};
    padding: 0.2rem;
    border-radius: 5px;
    :hover {
        filter: brightness(var(--brightness));
    }
`

const Wrapper = styled.div`
    overflow: hidden;
`

const GridColumn = styled.div`
    display: grid;
    grid-template-rows: auto auto auto auto auto 1fr;
    height: 100%;
    overflow-y: auto;
`

const Section = styled.div`
    display: flex;
    flex-direction: column;
    font-size: 0.9rem;
    border-bottom: 1px solid var(--primary-accent-color);
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
`

const Option = styled.div`
    display: flex;
    border-radius: 5px;
    align-items: center;
    justify-content: space-between;
    cursor: ${props => props.selectable && 'pointer'};
    padding: 0.25rem 0.5rem;
    background-color: var(--primary-color);
    :hover{
        filter: ${props => props.selectable && 'brightness(var(--brightness))'};
    }
`


const stepOptions = {
    '10': '10mins',
    '15': '15mins',
    '30': '30mins',
}

const timeOptions = {
    '0': '12am',
    '1': '1am',
    '2': '2am',
    '3': '3am',
    '4': '4am',
    '5': '5am',
    '6': '6am',
    '7': '7am',
    '8': '8am',
    '9': '9am',
    '10': '10am',
    '11': '11am',
    '12': '12pm',
    '13': '1pm',
    '14': '2pm',
    '15': '3pm',
    '16': '4pm',
    '17': '5pm',
    '18': '6pm',
    '19': '7pm',
    '20': '8pm',
    '21': '9pm',
    '22': '10pm',
    '23': '11pm',
}


const FilterOptions = props => {
    const {
        filters,
        onFilter,
        styles,
        view,
    } = props;
    const dispatch = useDispatch()
    const data = useSelector((state) => state.db.present.data && state.db.present.data.toJS())
    const user = useSelector(state => state.auth.user)
    const [showPriority, setPriority] = React.useState(true)
    const [showPages, setPages] = React.useState(true)

    ///////////////
    // Constants //
    ///////////////
    const toggleStyle = {
        backgroundColor: 'transparent',
        padding: '0.3rem', 
        fontSize: '1rem'
    }

    const tileStyle = {
        backgroundColor: styles['secondaryColor'] || 'var(--primary-color)',
        padding: '0.1rem 0.3rem'
    }

    const dropdownStyle = {
        backgroundColor: 'var(--secondary-color)',
        padding: '0.2rem 0.5rem',
        optionPadding: '0.5rem 1rem',
        minWidth: '5rem',
    }

    const defaultStyle = {
        primaryColor: 'var(--secondary-color)',
        secondaryColor: 'var(--primary-color)'
    }
    const updatedStyle = Object.assign({}, defaultStyle, styles)

    //////////////
    // HANDLERS //
    //////////////
    const onToggle = (key, value) => {
        const oldUser = {...user}
        let updatedUser = {...user}
        updatedUser[key] = value ? value : !updatedUser[key]
        dispatch(actionCreators.updateUser(updatedUser, oldUser))
    }


    ////////////////
    // COMPONENTS //
    ////////////////
    const renderPriority = () => {
        let elements = []
        console.log(filters)
        Object.values(itemContext.PRIORITY).map(priority => {
            const include = filters.priorities.includes(priority.toUpperCase())
            const text = priority[0] + priority.toLowerCase().slice(1)
            elements.push(
                <ListTile text={text} styles={tileStyle} 
                    icon={<Check filled={!!include} margin='0.1rem 0.3rem'
                        backgroundColor={include && itemContext.PRIORITY_COLOR[priority.toUpperCase()]}
                        borderColor={itemContext.PRIORITY_COLOR[priority.toUpperCase()]}
                        onSelect={() => {}}/>} 
                    onSelect={() => onFilter('PRIORITY', itemContext.PRIORITY[priority.toUpperCase()].toUpperCase())}/>
            )
        })
        return elements
    }

    const renderPages = () => {
        let elements = []
        const pageIDs = Object.keys(data['pages'])
        pageIDs.map(pageID => {
            const page = data['pages'][pageID]
            const text = page['page_name']
            const include = filters.pageIDs.includes(pageID) & page['status'] === 'ACTIVE'

            elements.push(
                <ListTile text={text} styles={tileStyle} 
                    icon={<Check filled={!!include} margin='0 0.3rem'
                            backgroundColor={include && 'var(--secondary-accent-color)'}
                            borderColor='var(--secondary-accent-color)'
                            onSelect={() => {}}/>} 
                    onSelect={() => onFilter('PAGES', pageID)}/>
            )
        })
        return elements
    }

    return (
        <Paper style={{margin: '0.5rem'}}>
            <Container styles={updatedStyle}>
                <GridColumn>
                    {/* General options */}
                    <Section>
                        {['week','work_week'].includes(view) &&
                        <Option selectable onClick={() => onToggle('calendar_show_weekends')}>
                            Show weekends
                            <Switch checked={user['calendar_show_weekends']}/>
                        </Option>}

                        <Option selectable onClick={() => onToggle('calendar_show_completed')}>
                            Show completed
                            <Switch checked={user['calendar_show_completed']}/>
                        </Option>

                        <Option>
                            Step size
                            <Dropdown options={stepOptions} style={dropdownStyle}
                                onSelect={(v) => onToggle('calendar_step_size', v)} 
                                value={stepOptions[user['calendar_step_size']]}/>
                        </Option>

                        <Option>
                            Initial time
                            <Dropdown options={timeOptions} style={dropdownStyle}
                                onSelect={(v) => onToggle('calendar_initial_time', v)} 
                                value={timeOptions[user['calendar_initial_time']]}/>
                        </Option>
                    </Section>
                    
                    {/* Priority filters */}
                    <Title onClick={() => setPriority(!showPriority)} styles={updatedStyle}>
                        <div style={{fontSize: '0.9rem', fontWeight: 'bolder', width: '100%', marginLeft: '0.3rem'}}>Priority</div>
                        <ToggleButton open={showPriority} 
                            styles={toggleStyle} 
                            icon={<FaAngleUp/>} end={180}/>
                    </Title>
                    
                    <Wrapper>
                        <AnimatePresence initial={false} key='priority'>
                            {showPriority && 
                            <Animation animation='expand'>
                                {renderPriority()}
                            </Animation>}
                        </AnimatePresence>
                    </Wrapper>

                    {/* Page filters */}
                    <Title onClick={() => setPages(!showPages)} styles={updatedStyle}>
                        <div style={{fontSize: '0.9rem', fontWeight: 'bolder', width: '100%', marginLeft: '0.3rem'}}>Pages</div>
                        <ToggleButton open={showPages} 
                            styles={toggleStyle} 
                            icon={<FaAngleUp/>} end={180}/>
                    </Title>
                    
                    <Wrapper>
                        <AnimatePresence initial={false} key='priority'>
                            {showPages && 
                            <Animation animation='expand'>
                                {renderPages()}
                            </Animation>}
                        </AnimatePresence>
                    </Wrapper>
                </GridColumn>

            </Container>
        </Paper>
    )
}

export default FilterOptions